import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Flip } from "react-toastify";
import api from "../axiosConfig";
import { SignupFormData } from "../types/authTypes";
import { mapApiErrorToField } from "../utils/errorMapper";

interface UseSignupHandlerProps {
  formData: SignupFormData;
  errors: Record<string, string | undefined>;
  setError: (field: keyof SignupFormData, message: string) => void;
  clearError: (field: keyof SignupFormData) => void;
}

export const useSignupHandler = ({ formData, errors, setError }: UseSignupHandlerProps) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getString = (val: string | string[]) => (Array.isArray(val) ? val[0] || "" : val);

  const isFormValid = useMemo(() => {
    const hasRequiredFields = getString(formData.user_name).trim() && getString(formData.organization_name).trim() && getString(formData.email_id).trim() && getString(formData.business_type).trim();
    const phoneNumber = getString(formData.phone_number).replace(/\D/g, "");
    const isPhoneValid = phoneNumber.length >= 8 && phoneNumber.length <= 12;
    const hasErrors = Object.values(errors).some((error) => Boolean(error));
    return Boolean(hasRequiredFields) && isPhoneValid && !hasErrors;
  }, [formData, errors]);

  useEffect(() => {
    const phoneNumber = getString(formData.phone_number).replace(/\D/g, "");
    console.log("Form Validation Status:", {
      hasRequiredFields: Boolean(
        getString(formData.user_name).trim() && getString(formData.organization_name).trim() && getString(formData.email_id).trim() && getString(formData.business_type).trim(),
      ),
      phoneLength: phoneNumber.length,
      isPhoneValid: phoneNumber.length >= 8 && phoneNumber.length <= 12,
      hasErrors: Object.values(errors).some(Boolean),
      errors: errors,
      isFormValid:
        Boolean(getString(formData.user_name).trim() && getString(formData.organization_name).trim() && getString(formData.email_id).trim() && getString(formData.business_type).trim()) &&
        phoneNumber.length >= 8 &&
        phoneNumber.length <= 12 &&
        !Object.values(errors).some(Boolean),
    });
  }, [formData, errors]);

  const handleRegister = async () => {
    const phoneNumber = getString(formData.phone_number).replace(/\D/g, "");
    if (phoneNumber.length < 8 || phoneNumber.length > 12) {
      toast.error("Phone number must be 8 to 12 digits", { theme: "colored" });
      setError("phone_number", "Phone number must be 8 to 12 digits");
      return;
    }

    if (!isFormValid) {
      toast.error("Please fix the errors before submitting", { theme: "colored" });
      return;
    }

    const normalizeEmails = (emails: (string | string[])[]) => {
      return emails.flatMap((e) => {
        if (Array.isArray(e)) return e;
        return e
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      });
    };

    const dataChange = {
      userName: formData.user_name,
      firstName: formData.name,
      middleName: "",
      lastName: "",
      orgName: formData.organization_name,
      orgArabicName: formData.org_Arabic_Name,
      businessLocation: formData.business_location,
      businessType: formData.business_type,
      dob: formData.date_of_birth,
      phones: [phoneNumber, formData.secondary_phone_number].filter(Boolean),
      addressLine1: formData.address_line_1,
      addressLine2: "",
      emails: normalizeEmails([formData.email_id, formData.secondary_email_id]),
      state: formData.state,
      pincode: formData.postal_code,
      country: formData.country,
      timeZoneId: formData.time_zone_id,
      roles: ["Owner"],
      imageUrl: "png",
    };

    setLoading(true);

    try {
      await api.post("/pos/v2/register", dataChange);

      toast.success("Account created successfully", { transition: Flip });
      setTimeout(() => navigate("/"), 1000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Unknown error";

      const mappedError = mapApiErrorToField(errorMessage);

      if (mappedError) {
        setError(mappedError.field, mappedError.message);
      } else {
        toast.error(errorMessage, { theme: "colored" });
      }
    } finally {
      setLoading(false);
    }
  };

  return { loading, isFormValid, handleRegister };
};
